import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Monitor, Smartphone, Globe, Trash2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import api from '../../lib/api';

interface SessionInfo {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
  is_current: boolean;
}

function parseDevice(userAgent: string | null): { icon: typeof Monitor; label: string } {
  if (!userAgent) return { icon: Globe, label: 'Unbekanntes Gerät' };
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone'))
    return { icon: Smartphone, label: 'Mobiles Gerät' };
  if (ua.includes('firefox')) return { icon: Monitor, label: 'Firefox' };
  if (ua.includes('chrome')) return { icon: Monitor, label: 'Chrome' };
  if (ua.includes('safari')) return { icon: Monitor, label: 'Safari' };
  if (ua.includes('edge')) return { icon: Monitor, label: 'Edge' };
  return { icon: Monitor, label: 'Browser' };
}

export default function SessionsTab() {
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery<SessionInfo[]>({
    queryKey: ['account-sessions'],
    queryFn: async () => (await api.get('/account/sessions')).data,
  });

  const revokeOne = useMutation({
    mutationFn: (id: string) => api.delete(`/account/sessions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sessions'] });
      toast.success('Sitzung beendet');
    },
    onError: () => toast.error('Fehler beim Beenden der Sitzung'),
  });

  const revokeOthers = useMutation({
    mutationFn: () => api.delete('/account/sessions/others'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sessions'] });
      toast.success('Alle anderen Sitzungen wurden beendet');
    },
    onError: () => toast.error('Fehler beim Beenden der Sitzungen'),
  });

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <Card variant="glass">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="font-display flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Aktive Sitzungen
            </CardTitle>
            <CardDescription className="mt-1">
              {sessions.length === 0
                ? 'Keine aktiven Sitzungen gefunden'
                : `${sessions.length} aktive Sitzung${sessions.length !== 1 ? 'en' : ''}`}
            </CardDescription>
          </div>
          {otherCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => revokeOthers.mutate()}
              disabled={revokeOthers.isPending}
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Alle anderen beenden
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Wird geladen…</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Keine aktiven Sitzungen gefunden.
          </p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const { icon: DeviceIcon, label: deviceLabel } = parseDevice(session.user_agent);
              return (
                <div
                  key={session.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                    session.is_current
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-border bg-card hover:bg-muted/40'
                  }`}
                >
                  <DeviceIcon
                    className={`h-5 w-5 mt-0.5 shrink-0 ${
                      session.is_current ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{deviceLabel}</span>
                      {session.is_current && (
                        <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">
                          Aktuelle Sitzung
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {session.ip_address ?? 'IP unbekannt'}
                      {session.last_used_at && (
                        <> · Zuletzt aktiv{' '}
                          {formatDistanceToNow(new Date(session.last_used_at), {
                            addSuffix: true,
                            locale: de,
                          })}
                        </>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate opacity-60">
                      {session.user_agent}
                    </p>
                  </div>
                  {!session.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-1.5 h-auto"
                      onClick={() => revokeOne.mutate(session.id)}
                      disabled={revokeOne.isPending}
                      title="Sitzung beenden"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
